export type Locale = "pt" | "en";

export interface NavLinks {
  about: string;
  skills: string;
  projects: string;
  experience: string;
  education: string;
  certifications: string;
  support?: string;
  contact: string;
}

export interface FocusCard {
  title: string;
  description: string;
}

export interface SkillCategory {
  id: string;
  title: string;
  description: string;
}

export interface TimelineItem {
  id: string;
  org: string;
  role: string;
  period: string;
  description: string;
  points: string[];
  stack: string[];
}

export interface EducationItem {
  id: string;
  institution: string;
  course: string;
  period: string;
  description: string;
}

export interface CertificationItem {
  id: string;
  name: string;
  issuer: string;
  date: string;
  topic: string;
}

export interface Translations {
  meta: {
    title: string;
    description: string;
  };
  nav: NavLinks & { menuOpen: string; menuClose: string };
  hero: {
    eyebrow: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
    ctaResume: string;
    scrollHint: string;
    statLabels: { location: string; languages: string; availability: string };
    statValues: { location: string; languages: string; availability: string };
    aboutEyebrow: string;
    aboutHeading: string;
    paragraphs: string[];
    philosophyLabel: string;
    philosophy: string;
    focusLabel: string;
    focusAreas: FocusCard[];
  };
  terminal: {
    title: string;
    whoamiCmd: string;
    whoamiOut: string;
    focusCmd: string;
    focusOut: string;
    buildingCmd: string;
    buildingOut: string;
    statusCmd: string;
    statusOut: string;
  };
  skills: {
    eyebrow: string;
    heading: string;
    subheading: string;
    categories: SkillCategory[];
  };
  projects: {
    eyebrow: string;
    heading: string;
    subheading: string;
    featuredLabel: string;
    featured: {
      name: string;
      tagline: string;
      description: string;
      status: string;
      codeLabel: string;
      liveLabel: string;
      privateLabel?: string;
      domainLabel: string;
      domainNote: string;
      previewNote: string;
    };
    featuredProjects: Record<
      "kyofuse" | "skillnapse",
      {
        name: string;
        tagline: string;
        description: string;
        status: string;
        codeLabel?: string;
        liveLabel: string;
        privateLabel?: string;
        domainLabel: string;
        domainNote: string;
        previewNote: string;
      }
    >;
    statusLive: string;
    statusProgress: string;
    statusDone: string;
    codeLabel: string;
    liveLabel: string;
    backendLabel: string;
    frontendLabel: string;
    secondaryLabel: string;
    secondary: Record<
      | "laylayStore"
      | "pocketmarket"
      | "caseAutou"
      | "mobileTicketApi"
      | "linkManagerApi"
      | "ecommerceAiAgent"
      | "woaRpg"
      | "meLevaAi"
      | "gamex"
      | "oldflix",
      { name: string; tagline: string; description: string; status: string; teamNote?: string }
    >;
  };
  experience: {
    eyebrow: string;
    heading: string;
    subheading: string;
    items: TimelineItem[];
    placeholder: string;
  };
  education: {
    eyebrow: string;
    heading: string;
    items: EducationItem[];
    placeholder: string;
  };
  certifications: {
    eyebrow: string;
    heading: string;
    items: CertificationItem[];
    placeholder: string;
  };
  support: {
    eyebrow: string;
    heading: string;
    subheading: string;
    cardTag: string;
    username: string;
    perks: string[];
    cta: string;
    note: string;
  };
  contact: {
    eyebrow: string;
    heading: string;
    subheading: string;
    emailLabel: string;
    phoneLabel: string;
    cta: string;
    availability: string;
  };
  footer: {
    role: string;
    rights: string;
    backToTop: string;
  };
}

export const translations: Record<Locale, Translations> = {
  pt: {
    meta: {
      title: "Murilo Marques — Desenvolvedor de Software",
      description:
        "Murilo Marques, desenvolvedor de software especializado em backend e full-stack. Java, Spring Boot, Angular e arquitetura de APIs.",
    },
    nav: {
      about: "Sobre",
      skills: "Habilidades",
      projects: "Projetos",
      experience: "Experiência",
      education: "Formação",
      certifications: "Certificações",
      support: "Apoie",
      contact: "Contato",
      menuOpen: "Abrir menu",
      menuClose: "Fechar menu",
    },
    hero: {
      eyebrow: "Desenvolvedor de Software · Backend & Full-Stack",
      subtitle:
        "Desenvolvedor de software back-end, construindo sistemas escaláveis com Java, Spring Boot e práticas modernas de engenharia.",
      ctaPrimary: "Ver Projetos",
      ctaSecondary: "Entrar em Contato",
      ctaResume: "Baixar Currículo",
      scrollHint: "Rolar",
      statLabels: {
        location: "Localização",
        languages: "Idiomas",
        availability: "Disponibilidade",
      },
      statValues: {
        location: "Recife, PE",
        languages: "PT · EN · ES",
        availability: "Aberto a oportunidades",
      },
      aboutEyebrow: "Sobre mim",
      aboutHeading: "Engenharia de software com atenção aos detalhes que importam.",
      paragraphs: [
        "Sou desenvolvedor de software com foco em back-end e desenvolvimento full-stack, com experiência na construção de aplicações web escaláveis usando Java e Spring Boot.",
        "Gosto de pensar em arquitetura antes de escrever a primeira linha de código: como os dados fluem, onde ficam as responsabilidades e como o sistema se comporta sob pressão. Boas decisões de design economizam tempo muito depois do primeiro commit.",
        "Tenho experiência com APIs RESTful, bancos de dados relacionais (PostgreSQL, MySQL), mensageria (Kafka, RabbitMQ, ActiveMQ) e ambientes de nuvem como AWS e Azure — sempre buscando aprofundar boas práticas de engenharia e qualidade de software.",
      ],
      philosophyLabel: "Abordagem",
      philosophy:
        "Código legível, decisões bem justificadas e sistemas pensados para durar — não apenas para funcionar hoje.",
      focusLabel: "Áreas de atuação",
      focusAreas: [
        {
          title: "Backend Development",
          description: "APIs robustas e serviços escaláveis com Java e Spring Boot.",
        },
        {
          title: "Full-Stack Development",
          description: "Do banco de dados à interface, com Angular no front-end.",
        },
        {
          title: "API Architecture",
          description: "Design de contratos claros, versionamento e boas práticas REST.",
        },
        {
          title: "Database Design",
          description: "Modelagem relacional consistente com PostgreSQL e MySQL.",
        },
        {
          title: "Cloud & Messaging",
          description: "Ambientes AWS e Azure com Kafka, RabbitMQ e ActiveMQ.",
        },
        {
          title: "Software Engineering",
          description: "Código limpo, versionamento disciplinado e arquitetura sustentável.",
        },
      ],
    },
    terminal: {
      title: "sessão — zsh",
      whoamiCmd: "whoami",
      whoamiOut: "Murilo Henrique Marques Bento Santos",
      focusCmd: "focus",
      focusOut: "Backend & Full-Stack Development",
      buildingCmd: "currently_building",
      buildingOut: "Kyofuse — plataforma social para a comunidade CS2 (kyofuse.com)",
      statusCmd: "status",
      statusOut: "disponível para novas oportunidades",
    },
    skills: {
      eyebrow: "Habilidades técnicas",
      heading: "Ferramentas e tecnologias do dia a dia.",
      subheading:
        "Uma stack pensada para construir sistemas back-end sólidos sem perder capacidade de entregar experiências completas no front-end.",
      categories: [
        { id: "backend", title: "Backend", description: "Serviços e APIs" },
        { id: "frontend", title: "Frontend", description: "Interfaces e experiência" },
        { id: "messaging", title: "Mensageria", description: "Comunicação assíncrona" },
        { id: "database", title: "Banco de Dados", description: "Modelagem e dados" },
        { id: "devops", title: "DevOps / Cloud", description: "Ambientes e nuvem" },
        { id: "tools", title: "Ferramentas", description: "Fluxo de trabalho" },
      ],
    },
    projects: {
      eyebrow: "Projetos selecionados",
      heading: "Produtos e sistemas em produção e destaque.",
      subheading:
        "Um recorte dos projetos que melhor representam a forma como penso e construo software.",
      featuredLabel: "Projeto em destaque",
      featured: {
        name: "Kyofuse",
        tagline: "Plataforma social para a comunidade competitiva de CS2",
        description:
          "Rede social focada no cenário de Counter-Strike 2, conectando jogadores e equipes em um ambiente competitivo e dinâmico. A plataforma centraliza perfis de jogadores com integração à Steam, feed de publicações com interações da comunidade, gestão completa de squads e convites para times, grafo social com controle de privacidade e comunicação em tempo real com mensagens instantâneas e notificações. Desenvolvida em arquitetura modular com Spring Boot e Angular, priorizando alta disponibilidade, desempenho e uma experiência fluida para o usuário.",
        status: "Concluído • No ar (Fase de testes)",
        codeLabel: "Código",
        liveLabel: "Acessar plataforma",
        privateLabel: "Repositório privado",
        domainLabel: "kyofuse.com",
        domainNote: "Fase de testes",
        previewNote: "Tela de feed — kyofuse.com",
      },
      featuredProjects: {
        kyofuse: {
          name: "Kyofuse",
          tagline: "Plataforma social para a comunidade competitiva de CS2",
          description:
            "Rede social focada no cenário de Counter-Strike 2, conectando jogadores e equipes em um ambiente competitivo e dinâmico. A plataforma centraliza perfis de jogadores com integração à Steam, feed de publicações com interações da comunidade, gestão completa de squads e convites para times, grafo social com controle de privacidade e comunicação em tempo real com mensagens instantâneas e notificações. Desenvolvida em arquitetura modular com Spring Boot e Angular, priorizando alta disponibilidade, desempenho e uma experiência fluida para o usuário.",
          status: "Concluído • No ar (Fase de testes)",
          codeLabel: "Código",
          liveLabel: "Acessar plataforma",
          privateLabel: "Repositório privado",
          domainLabel: "kyofuse.com",
          domainNote: "Fase de testes",
          previewNote: "Tela de feed — kyofuse.com",
        },
        skillnapse: {
          name: "SkillNapse",
          tagline: "Plataforma de alta performance para orquestração de estudos com Inteligência Artificial",
          description:
            "Ecossistema educacional completo voltado para preparação de alta performance em vestibulares e concursos. Conta com hierarquia dinâmica de conhecimentos extraída de editais via IA, cronogramas adaptativos de estudos, simulados calibrados com detecção de lacunas de aprendizagem, repetição espaçada com algoritmo SuperMemo SM-2 e laboratório dissertativo com auditoria textual automatizada e nota por competências oficiais. Arquitetura orientada a domínios com Spring Boot 4, contingência inteligente multi-provedores de IA (Google Gemini e Groq) e persistência relacional com PostgreSQL.",
          status: "Concluído • No ar",
          codeLabel: "Backend",
          liveLabel: "Acessar plataforma",
          domainLabel: "skillnapse.me",
          domainNote: "Produção",
          previewNote: "Plataforma web — skillnapse.me",
        },
      },
      statusLive: "No ar",
      statusProgress: "Em desenvolvimento",
      statusDone: "Concluído",
      codeLabel: "Código",
      liveLabel: "Acessar plataforma",
      backendLabel: "Backend",
      frontendLabel: "Frontend",
      secondaryLabel: "Outros projetos",
      secondary: {
        laylayStore: {
          name: "Lay-Lay Store",
          tagline: "E-commerce mobile-first para colecionadores e artigos Pokémon",
          description:
            "Plataforma de comércio eletrônico leve e reativa desenvolvida em React 19, TypeScript, Vite e Tailwind CSS, especializada em cartas de Pokémon TCG, pelúcias e acessórios artesanais. Possui sacola de compras com cálculo dinâmico de frete grátis, checkout direto integrado à API do WhatsApp, filtros e busca em tempo real, dock inferior para navegação mobile e painel administrativo com persistência no Supabase.",
          status: "No ar",
        },
        pocketmarket: {
          name: "PocketMarket",
          tagline: "Marketplace para colecionadores de Pokémon TCG",
          description:
            "Projeto em grupo para a disciplina de Back-end Frameworks (UNINASSAU): um marketplace para gerenciar e negociar cartas do Pokémon TCG, com autenticação JWT, coleções pessoais, favoritos, venda direta, leilões e propostas de troca. Minha parte cobriu upload de imagens, integração com a Pokémon TCG API e os sistemas de compra, leilão e anúncios.",
          status: "Concluído",
          teamNote: "Desenvolvido com mais 3 colegas de turma",
        },
        caseAutou: {
          name: "Analisador de E-mails com IA",
          tagline: "Case prático da AutoU",
          description:
            "Case técnico que classifica e-mails como produtivos ou improdutivos usando a API Gemini do Google e sugere uma resposta contextual. Aceita texto colado ou upload de .txt/.pdf com drag-and-drop, com back-end em Flask e front-end em JavaScript puro.",
          status: "Concluído",
        },
        mobileTicketApi: {
          name: "Sistema de Controle de Atendimento",
          tagline: "API de gerenciamento de filas e senhas",
          description:
            "API REST para gerenciamento de filas de atendimento, feita para um projeto de disciplina na UNINASSAU. Gerencia guichês, emite senhas, chama a próxima e mantém histórico de chamadas, com rotas protegidas por JWT e banco MySQL via Sequelize.",
          status: "Concluído",
        },
        linkManagerApi: {
          name: "Link Manager API",
          tagline: "API REST para gerenciamento de links",
          description:
            "API REST enxuta para organizar e gerenciar uma coleção de links, com CRUD completo, armazenamento em PostgreSQL, testes automatizados com Pytest e ambiente containerizado com Docker.",
          status: "Concluído",
        },
        ecommerceAiAgent: {
          name: "Agente IA para E-commerce",
          tagline: "Agente de IA integrado a um e-commerce via MCP",
          description:
            "TCC da formação CWI Crescer (Edição 28): um e-commerce orientado a eventos (JMS) com um agente de IA integrado via Model Context Protocol (MCP), que consome contexto de clientes e catálogo para gerar recomendações, explicações e insights de comportamento e tendências. Minha parte cobriu o processamento de eventos de cliente, pedido e produto, além dos serviços de recomendação e integração com IA/MCP no backend, com deploy contínuo em Kubernetes via Helm.",
          status: "Concluído",
          teamNote: "Desenvolvido com mais colegas de turma (TCC em grupo)",
        },
        woaRpg: {
          name: "WoA — RPG Web",
          tagline: "RPG web com sistema de batalhas e missões",
          description:
            "TCC da formação CWI Crescer (Edição 28): um RPG web com criação de personagens, raças, loja de itens e sistema de batalhas e missões, com autenticação JWT e rotas protegidas. Minha parte cobriu as páginas de Batalha e Missões, os modelos de personagem e raça, e componentes reutilizáveis de interface.",
          status: "Concluído",
          teamNote: "Desenvolvido com mais 2 colegas de turma",
        },
        meLevaAi: {
          name: "Me Leva Aí",
          tagline: "App de solicitação de corridas com gestão de saldo",
          description:
            "Projeto em grupo da formação CWI Crescer (Edição 28): uma plataforma de solicitação de corridas com cadastro de passageiros e motoristas, ciclo completo de corrida e avaliação bidirecional, além de um sistema de saldo com depósito e saque. Minha parte cobriu a lógica de início de corrida, a validação de saldo insuficiente, o fluxo de depósito e saque, e a finalização da aba de corridas no front-end.",
          status: "Concluído",
          teamNote: "Desenvolvido com mais 2 colegas de turma",
        },
        gamex: {
          name: "GameX",
          tagline: "Rede social para gamers",
          description:
            "TCC individual da formação CWI Crescer: uma rede social para jogadores, com feed de posts, curtidas, comentários, sistema de amizades e registro de jogos platinados. Autenticação via JWT com chaves RSA, tema claro/escuro responsivo e cobertura mínima de 80% em testes unitários no backend.",
          status: "Concluído",
        },
        oldflix: {
          name: "OldFlix",
          tagline: "Catálogo e locação de filmes",
          description:
            "API e frontend para gestão de um catálogo de locadora de filmes: listagem, cadastro, edição, remoção, aluguel e devolução de títulos, com PostgreSQL e Docker Compose para o banco de dados.",
          status: "Concluído",
        },
      },
    },
    experience: {
      eyebrow: "Trajetória",
      heading: "Experiência profissional.",
      subheading: "Um histórico em construção — atualizado conforme novas experiências acontecem.",
      items: [
        {
          id: "exp-cwi",
          org: "CWI Software",
          role: "Estagiário de Desenvolvimento de Software",
          period: "Jun 2025 — Mai 2026",
          description:
            "Estágio em desenvolvimento de software na CWI Software, construindo aplicações back-end modernas com Java e Spring Boot.",
          points: [
            "Desenvolvimento de aplicações back-end com Java e Spring Boot seguindo práticas modernas de engenharia de software.",
            "Implementação de APIs RESTful e integrações com banco de dados usando PostgreSQL e MySQL.",
            "Colaboração em times ágeis utilizando Git, Docker e fluxos de trabalho modernos de desenvolvimento.",
          ],
          stack: ["Java", "Spring Boot", "PostgreSQL", "MySQL", "Docker", "Git"],
        },
        {
          id: "exp-army",
          org: "Exército Brasileiro",
          role: "Soldado",
          period: "Mar 2023 — Mar 2024",
          description:
            "Serviço militar no Exército Brasileiro, desenvolvendo disciplina, trabalho em equipe e liderança em ambientes de alta pressão.",
          points: [
            "Realização de atividades administrativas e operacionais mantendo disciplina e responsabilidade.",
            "Desenvolvimento de trabalho em equipe, liderança e resolução de problemas em ambientes de alta pressão.",
            "Apoio às operações organizacionais garantindo conformidade com os procedimentos estabelecidos.",
          ],
          stack: [],
        },
      ],
      placeholder: "Experiências adicionais serão listadas aqui conforme forem adicionadas.",
    },
    education: {
      eyebrow: "Formação",
      heading: "Educação e formação acadêmica.",
      items: [
        {
          id: "edu-uninassau",
          institution: "UNINASSAU",
          course: "Tecnólogo em Análise e Desenvolvimento de Sistemas",
          period: "Previsão de conclusão: Jul 2027",
          description:
            "Curso tecnólogo com foco em desenvolvimento de software, análise de sistemas e banco de dados.",
        },
        {
          id: "edu-cwi-crescer",
          institution: "CWI Crescer — Edição 28",
          course: "Programa de Formação em Desenvolvimento de Software",
          period: "—",
          description:
            "Programa intensivo de formação em desenvolvimento de software pela CWI, com foco em fundamentos de back-end e boas práticas.",
        },
      ],
      placeholder: "Formações adicionais serão listadas aqui conforme forem adicionadas.",
    },
    certifications: {
      eyebrow: "Certificações",
      heading: "Cursos e certificações.",
      items: [],
      placeholder: "Novas certificações serão adicionadas aqui.",
    },
    support: {
      eyebrow: "Apoio & Doações",
      heading: "Gostou do meu trabalho? Pague um café.",
      subheading:
        "Sua contribuição ajuda a manter a infraestrutura de servidores ativos (Kyofuse, SkillNapse), cobrir cotas de APIs e impulsionar novos projetos de código aberto.",
      cardTag: "Apoie no Ko-fi",
      username: "ko-fi.com/murilo_marques",
      perks: [
        "☕ Pague um cafézinho",
        "☁️ Custos de hospedagem & APIs",
        "🚀 Incentivo a projetos open-source",
      ],
      cta: "Fazer uma doação no Ko-fi",
      note: "Doações rápidas e seguras via Cartão, PayPal ou Pix.",
    },
    contact: {
      eyebrow: "Contato",
      heading: "Vamos construir algo juntos.",
      subheading:
        "Estou aberto a novas oportunidades, colaborações e conversas sobre projetos interessantes. Envie uma mensagem.",
      emailLabel: "E-mail",
      phoneLabel: "Telefone",
      cta: "Enviar mensagem",
      availability: "Disponível para oportunidades",
    },
    footer: {
      role: "Desenvolvedor de Software",
      rights: "Todos os direitos reservados.",
      backToTop: "Voltar ao topo",
    },
  },
  en: {
    meta: {
      title: "Murilo Marques — Software Developer",
      description:
        "Murilo Marques, software developer specialized in backend and full-stack engineering. Java, Spring Boot, Angular, and API architecture.",
    },
    nav: {
      about: "About",
      skills: "Skills",
      projects: "Projects",
      experience: "Experience",
      education: "Education",
      certifications: "Certifications",
      support: "Support",
      contact: "Contact",
      menuOpen: "Open menu",
      menuClose: "Close menu",
    },
    hero: {
      eyebrow: "Software Developer · Backend & Full-Stack",
      subtitle:
        "Backend software developer building scalable systems with Java, Spring Boot, and modern engineering practices.",
      ctaPrimary: "View Projects",
      ctaSecondary: "Contact Me",
      ctaResume: "Download Resume",
      scrollHint: "Scroll",
      statLabels: {
        location: "Location",
        languages: "Languages",
        availability: "Availability",
      },
      statValues: {
        location: "Recife, Brazil",
        languages: "PT · EN · ES",
        availability: "Open to opportunities",
      },
      aboutEyebrow: "About me",
      aboutHeading: "Software engineering with attention to the details that matter.",
      paragraphs: [
        "I'm a software developer focused on backend and full-stack development, with experience building scalable web applications using Java and Spring Boot.",
        "I like thinking about architecture before writing the first line of code: how data flows, where responsibilities live, and how the system behaves under pressure. Good design decisions pay off long after the first commit.",
        "I have hands-on experience with RESTful APIs, relational databases (PostgreSQL, MySQL), messaging systems (Kafka, RabbitMQ, ActiveMQ), and cloud environments like AWS and Azure — always looking to deepen best engineering practices and software quality.",
      ],
      philosophyLabel: "Approach",
      philosophy:
        "Readable code, well-justified decisions, and systems built to last — not just to work today.",
      focusLabel: "Areas of focus",
      focusAreas: [
        {
          title: "Backend Development",
          description: "Robust APIs and scalable services with Java and Spring Boot.",
        },
        {
          title: "Full-Stack Development",
          description: "From the database to the interface, with Angular on the front-end.",
        },
        {
          title: "API Architecture",
          description: "Clear contract design, versioning, and REST best practices.",
        },
        {
          title: "Database Design",
          description: "Consistent relational modeling with PostgreSQL and MySQL.",
        },
        {
          title: "Cloud & Messaging",
          description: "AWS and Azure environments with Kafka, RabbitMQ, and ActiveMQ.",
        },
        {
          title: "Software Engineering",
          description: "Clean code, disciplined versioning, and sustainable architecture.",
        },
      ],
    },
    terminal: {
      title: "session — zsh",
      whoamiCmd: "whoami",
      whoamiOut: "Murilo Henrique Marques Bento Santos",
      focusCmd: "focus",
      focusOut: "Backend & Full-Stack Development",
      buildingCmd: "currently_building",
      buildingOut: "Kyofuse — social platform for the CS2 community (kyofuse.com)",
      statusCmd: "status",
      statusOut: "open to new opportunities",
    },
    skills: {
      eyebrow: "Technical skills",
      heading: "Tools and technologies I work with.",
      subheading:
        "A stack built to construct solid back-end systems without losing the ability to ship complete front-end experiences.",
      categories: [
        { id: "backend", title: "Backend", description: "Services & APIs" },
        { id: "frontend", title: "Frontend", description: "Interfaces & experience" },
        { id: "messaging", title: "Messaging", description: "Asynchronous communication" },
        { id: "database", title: "Database", description: "Modeling & data" },
        { id: "devops", title: "DevOps / Cloud", description: "Environments & cloud" },
        { id: "tools", title: "Tools", description: "Daily workflow" },
      ],
    },
    projects: {
      eyebrow: "Selected projects",
      heading: "Featured products and systems.",
      subheading: "A selection of the projects that best represent how I think and build software.",
      featuredLabel: "Featured project",
      featured: {
        name: "Kyofuse",
        tagline: "Social platform for the competitive CS2 community",
        description:
          "A dedicated social network for the Counter-Strike 2 competitive ecosystem, connecting players and squads in a dynamic environment. The platform offers Steam-integrated player profiles, an interactive feed for community posts and interactions, team management with squad invitations, a comprehensive social graph with privacy controls, and real-time communication with instant chat and notifications. Built with a modular architecture using Spring Boot and Angular, emphasizing high availability, performance, and a smooth user experience.",
        status: "Completed • Live (Testing Phase)",
        codeLabel: "Code",
        liveLabel: "Visit platform",
        privateLabel: "Private repository",
        domainLabel: "kyofuse.com",
        domainNote: "Testing phase",
        previewNote: "Feed screen — kyofuse.com",
      },
      featuredProjects: {
        kyofuse: {
          name: "Kyofuse",
          tagline: "Social platform for the competitive CS2 community",
          description:
            "A dedicated social network for the Counter-Strike 2 competitive ecosystem, connecting players and squads in a dynamic environment. The platform offers Steam-integrated player profiles, an interactive feed for community posts and interactions, team management with squad invitations, a comprehensive social graph with privacy controls, and real-time communication with instant chat and notifications. Built with a modular architecture using Spring Boot and Angular, emphasizing high availability, performance, and a smooth user experience.",
          status: "Completed • Live (Testing Phase)",
          codeLabel: "Code",
          liveLabel: "Visit platform",
          privateLabel: "Private repository",
          domainLabel: "kyofuse.com",
          domainNote: "Testing phase",
          previewNote: "Feed screen — kyofuse.com",
        },
        skillnapse: {
          name: "SkillNapse",
          tagline: "High-performance study orchestration & adaptive learning platform powered by Spring AI",
          description:
            "Comprehensive EdTech ecosystem engineered for rigorous exam and test preparation. Features syllabus hierarchy extraction from PDFs using AI, adaptive daily schedules, AI-generated diagnostic mock exams targeting knowledge gaps, Spaced Repetition (SRS) via the SuperMemo SM-2 algorithm, and an automated essay grading laboratory assessing official scoring criteria. Built as a domain-driven backend with Spring Boot 4, resilient multi-provider AI contingency (Google Gemini & Groq), and PostgreSQL persistence.",
          status: "Completed • Live",
          codeLabel: "Backend",
          liveLabel: "Visit platform",
          domainLabel: "skillnapse.me",
          domainNote: "Production",
          previewNote: "Web platform — skillnapse.me",
        },
      },
      statusLive: "Live",
      statusProgress: "In development",
      statusDone: "Completed",
      codeLabel: "Code",
      liveLabel: "Visit platform",
      backendLabel: "Backend",
      frontendLabel: "Frontend",
      secondaryLabel: "Other projects",
      secondary: {
        laylayStore: {
          name: "Lay-Lay Store",
          tagline: "Mobile-first e-commerce for Pokémon TCG and collectibles",
          description:
            "Fast, responsive e-commerce web application built with React 19, TypeScript, Vite, and Tailwind CSS for Pokémon TCG cards, plushies, and handmade accessories. Includes animated cart with dynamic free-shipping progress, instant WhatsApp checkout, real-time live search, a bottom mobile dock, and store management backed by Supabase.",
          status: "Live",
        },
        pocketmarket: {
          name: "PocketMarket",
          tagline: "Marketplace for Pokémon TCG collectors",
          description:
            "A group project for the Back-end Frameworks course (UNINASSAU): a marketplace for managing and trading Pokémon TCG cards, with JWT auth, personal collections, favorites, direct sales, auctions and trade proposals. My part covered image uploads, the Pokémon TCG API integration, and the purchase, auction and listing systems.",
          status: "Completed",
          teamNote: "Built with 3 classmates",
        },
        caseAutou: {
          name: "AI Email Analyzer",
          tagline: "AutoU technical case study",
          description:
            "A technical case study that classifies emails as productive or unproductive using Google's Gemini API and suggests a contextual reply. Accepts pasted text or .txt/.pdf uploads with drag-and-drop, built with a Flask backend and a vanilla JS front-end.",
          status: "Completed",
        },
        mobileTicketApi: {
          name: "Service Queue Control System",
          tagline: "Queue and ticket management API",
          description:
            "A REST API for managing service queues, built for a UNINASSAU course project. Handles service windows, issues tickets, calls the next number and keeps a call history, with JWT-protected routes and a MySQL database via Sequelize.",
          status: "Completed",
        },
        linkManagerApi: {
          name: "Link Manager API",
          tagline: "REST API for link management",
          description:
            "A lean REST API for organizing and managing a collection of links, with full CRUD, PostgreSQL storage, automated tests with Pytest and a Dockerized setup.",
          status: "Completed",
        },
        ecommerceAiAgent: {
          name: "AI Agent for E-commerce",
          tagline: "AI agent integrated into an e-commerce platform via MCP",
          description:
            "Capstone project for the CWI Crescer training program (Edition 28): an event-driven (JMS) e-commerce platform with an AI agent integrated via the Model Context Protocol (MCP), pulling customer and catalog context to generate recommendations, explanations and behavior/trend insights. My part covered the customer, order and product event processing, plus the recommendation and AI/MCP integration services on the backend, with continuous deployment to Kubernetes via Helm.",
          status: "Completed",
          teamNote: "Built with classmates (group capstone project)",
        },
        woaRpg: {
          name: "WoA — Web RPG",
          tagline: "Web RPG with a battle and quest system",
          description:
            "Capstone project for the CWI Crescer training program (Edition 28): a web RPG with character creation, races, an item shop, and a battle and quest system, with JWT auth and protected routes. My part covered the Battle and Quest pages, the character and race models, and reusable UI components.",
          status: "Completed",
          teamNote: "Built with 2 classmates",
        },
        meLevaAi: {
          name: "Me Leva Aí",
          tagline: "Ride-hailing app with balance management",
          description:
            "A group project for the CWI Crescer training program (Edition 28): a ride-hailing platform with rider and driver registration, a full ride lifecycle with two-way ratings, and a balance system with deposits and withdrawals. My part covered the ride-start logic, insufficient-balance validation, the deposit/withdrawal flow, and finishing the rides tab on the front-end.",
          status: "Completed",
          teamNote: "Built with 2 classmates",
        },
        gamex: {
          name: "GameX",
          tagline: "Social network for gamers",
          description:
            "Individual capstone for the CWI Crescer training program: a social network for gamers, with a post feed, likes, comments, a friend system, and a tracker for platinum trophies. JWT authentication with RSA keys, a responsive light/dark theme, and a minimum 80% unit test coverage on the backend.",
          status: "Completed",
        },
        oldflix: {
          name: "OldFlix",
          tagline: "Movie catalog and rental management",
          description:
            "An API and frontend for managing a movie rental store's catalog: listing, registering, editing, removing, renting and returning titles, with PostgreSQL and Docker Compose for the database.",
          status: "Completed",
        },
      },
    },
    experience: {
      eyebrow: "Track record",
      heading: "Professional experience.",
      subheading: "A history in progress — updated as new experience happens.",
      items: [
        {
          id: "exp-cwi",
          org: "CWI Software",
          role: "Software Development Intern",
          period: "Jun 2025 — May 2026",
          description:
            "Software development internship at CWI Software, building modern backend applications with Java and Spring Boot.",
          points: [
            "Developed backend applications using Java and Spring Boot following modern software engineering practices.",
            "Implemented RESTful APIs and database integrations using PostgreSQL and MySQL.",
            "Collaborated in Agile teams using Git, Docker, and modern software development workflows.",
          ],
          stack: ["Java", "Spring Boot", "PostgreSQL", "MySQL", "Docker", "Git"],
        },
        {
          id: "exp-army",
          org: "Brazilian Army",
          role: "Soldier",
          period: "Mar 2023 — Mar 2024",
          description:
            "Military service in the Brazilian Army, developing discipline, teamwork, and leadership under high-pressure conditions.",
          points: [
            "Performed administrative and operational activities while maintaining discipline and responsibility.",
            "Developed teamwork, leadership, and problem-solving skills in high-pressure environments.",
            "Supported organizational operations while ensuring compliance with established procedures.",
          ],
          stack: [],
        },
      ],
      placeholder: "Additional experience will be listed here as it's added.",
    },
    education: {
      eyebrow: "Education",
      heading: "Academic background.",
      items: [
        {
          id: "edu-uninassau",
          institution: "UNINASSAU",
          course: "Associate Degree in Systems Analysis and Development",
          period: "Expected Jul 2027",
          description:
            "Associate degree program covering software development, systems analysis, and database design.",
        },
        {
          id: "edu-cwi-crescer",
          institution: "CWI Crescer — Edition 28",
          course: "Software Development Program",
          period: "—",
          description:
            "Intensive software development training program by CWI, focused on backend engineering fundamentals and best practices.",
        },
      ],
      placeholder: "Additional education will be listed here as it's added.",
    },
    certifications: {
      eyebrow: "Certifications",
      heading: "Courses and certifications.",
      items: [],
      placeholder: "New certifications will be added here.",
    },
    support: {
      eyebrow: "Support & Donations",
      heading: "Enjoying my work? Buy me a coffee.",
      subheading:
        "Your contribution helps maintain live server infrastructure (Kyofuse, SkillNapse), cover cloud API costs, and fuel new independent open-source projects.",
      cardTag: "Support on Ko-fi",
      username: "ko-fi.com/murilo_marques",
      perks: [
        "☕ Buy me a coffee",
        "☁️ Server & API hosting costs",
        "🚀 Fuel independent open-source tools",
      ],
      cta: "Support me on Ko-fi",
      note: "Fast and secure donations via Credit Card, PayPal or Pix.",
    },
    contact: {
      eyebrow: "Contact",
      heading: "Let's build something.",
      subheading:
        "I'm open to new opportunities, collaborations, and conversations about interesting projects. Send a message.",
      emailLabel: "Email",
      phoneLabel: "Phone",
      cta: "Send a message",
      availability: "Available for opportunities",
    },
    footer: {
      role: "Software Developer",
      rights: "All rights reserved.",
      backToTop: "Back to top",
    },
  },
};
